<template>
    <div>
        <main-modal 
            :show-modal="showModal" 
            size="sm" 
            :title="title" 
            @close="closeModal"
        >
            <template #body>
                <form @submit.prevent>
                    <FormInput
                        v-if="hasField('fullName')" 
                        id="fullName"
                        v-model="form.fullName"
                        size="sm"
                        autocomplete="" label="Full name"
                    />

                    <FormInput
                        v-if="hasField('brandName')" 
                        id="brandName"
                        v-model="form.brandName"
                        size="sm"
                        autocomplete="" 
                        label="Brand name"
                    />

                    <FormTextArea
                        v-if="hasField('bio')" 
                        id="bio"
                        v-model="form.bio"
                        size="sm"
                        class="mb--15"
                        autocomplete="" 
                        label="Bio"
                    />
                    
                    <FormInput
                        v-if="hasField('phoneNumber')" 
                        id="phoneNumber"
                        v-model="form.phoneNumber"
                        size="sm"
                        autocomplete="" 
                        label="Phone Number"
                    />

                    <div v-if="hasField('occupation')" class="mb--15">
                        <FormLabel title="Occupation" html-for="occupation" />
                        <CustomDropdown 
                            id="occupationDropdown" 
                            v-model="form.occupation" 
                            :options="occupationList"
                            class="pl--3 text--capitalize"
                            btn-classes="text--capitalize text--normal" 
                            list-item-class="text--capitalize" 
                            @search="searchOccupation" 
                            @select="v => form.occupation = v" 
                        />
                    </div>

                    <FormInput
                        v-if="hasField('address')" 
                        id="address"
                        v-model="form.address"
                        size="sm" 
                        autocomplete="" 
                        label="Address"
                    />

                    <FormInput
                        v-if="hasField('country')" 
                        id="country"
                        v-model="form.country"
                        size="sm" 
                        autocomplete="" 
                        label="Country"
                    />

                    <FormInput
                        v-if="hasField('zipCode')" 
                        id="zipCode"
                        v-model="form.zipCode"
                        size="sm" 
                        autocomplete="" 
                        label="Zip code"
                        no-margin-bottom
                    />

                    <FormInput 
                        v-if="hasField('email')"
                        id="newEmail" 
                        v-model="form.email" 
                        type="email"
                        disabled
                        readonly 
                        size="sm" 
                        label="Current email address" 
                    />
                    
                    <FormInput 
                        v-if="hasField('email')"
                        id="newEmail" 
                        v-model="newEmail.value" 
                        type="email"
                        required 
                        size="sm" 
                        no-margin-bottom
                        label="New email address" 
                        :is-error="newEmail.isError" 
                        :err-msg="newEmail.errMsg"
                    />
                </form>
            </template>

            <template #footer>
                <div class="w-100 flex justify-content-end align-items-center">
                    <BaseButton 
                        label="Cancel" 
                        variant="outline" 
                        class="mr--5" 
                        :disabled="loading"
                        @submit="closeModal"
                    />
                    <BaseButton label="Save" :loading="loading" @submit="handleUpdateProfile" />
                </div>
            </template>
        </main-modal>
    </div>
</template>

<script>
import countryList from "@/assets/js/countrycodes.json";
import { updateProfileFields } from "@/components/dataComponents/auth";
import MainModal from "./MainModal.vue";
import BaseButton from "../buttons/BaseButton.vue";
import CustomDropdown from "../dropdowns/CustomDropdown.vue";
import FormInput from "../input/FormInput.vue";
import FormTextArea from "../input/FormTextArea.vue";
import FormLabel from "../forms/FormLabel.vue";
import { occupationList } from "@/utils/dummy";

export default {
    name: "UpdateProfile",

    components: {
        MainModal,
        BaseButton,
        FormInput,
        FormLabel,
        CustomDropdown,
        FormTextArea,
    },

    props: {
        currentSelection: {
            type: String,
            default: "",
        },
        showModal: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },

    emits: ["submit", "cancel"],

    data() {
        return {
            countryList,
            occupationList: occupationList,
            accountModalFields: updateProfileFields,
            form: {
                fullName: "",
                brandName: "",
                phoneNumber: "",
                address: "",
                country: "",
                zipCode: "",  
                email: "",
                occupation: "",
                bio: "",
            },
            newEmail: {
                value: "",
                errMsg: "",
                isErr: false,
            }, 
            formFields: {
                fullName: {
                    fields: {
                        fullName: true,
                        brandName: true,
                        occupation: true,
                        bio: true,
                    },
                },
                contactDetails: {
                    fields: {
                        phoneNumber: true,
                        address: true,
                        country: true,
                        zipCode: true,  
                    },
                },
            },
        };
    },

    computed: {
        profile() {
            return this.$store.getters["auth/profile"];
        },

        title() {
            return this.accountModalFields[this.currentSelection]?.title || "";
        },
    },

    watch: {
        currentSelection(newVal) {            
            if (newVal) {
                if (this.accountModalFields[newVal] && this.accountModalFields[newVal]?.fields) {
                    this.populateFormBasedOnSelection(newVal);
                }
            }
        },
    },

    mounted() {
        this.prefillForm(this.profile);
    },

    methods: {
        hasField(field) {
            return !this.accountModalFields[this.currentSelection] ? false : this.accountModalFields[this.currentSelection].fields[field]; 
        },

        prefillForm(data) {            
            this.form = {
                ...this.form,
                ...data,
            };
        },

        populateFormBasedOnSelection(val) {
            const selectedItem = this.formFields[val]?.fields;

            for (const key in selectedItem) {
                if (this.profile.hasOwnProperty(key)) {
                    this.form[key] = this.profile[key];
                }
            }
        },

        resetForm() {
            this.form = {
                fullName: "",
                phoneNumber: "",
                address: "",
                country: "",
                zipCode: "",  
                email: "",
            };
        },

        async handleUpdateProfile() {
            const payload = {};

            const selectedItem = this.accountModalFields[this.currentSelection]?.fields;

            for (const key in selectedItem) {
                if (this.form.hasOwnProperty(key)) {
                    payload[key] = this.form[key];
                }
            }
            
            this.$emit("submit", payload);
        },

        closeModal() {
            this.$emit("cancel");
        },

        validateEmailInput(e) {
            const value = e?.target?.value;

            if (!value.includes("@")) return;

            const res = validateEmail(value);
             
            if (!res) {
                this.isEmailInvalid = true;
            } else {
                this.isEmailInvalid = false;
            }
        },

        searchOccupation(val) {            
            this.occupationList = occupationList.filter(item => item.toLowerCase().includes(val));
        },
    },
};
</script>